import {
  setPushpinAttributesAction,
  updateCurrentIssuePushpinAttributes,
  viewerExistingIssuePushpinThumbnailCreated,
  viewerPushpinThumbnailCreated
} from '../../app/actions';
import i18n from '../../utils/i18n';
import lodashGet from 'lodash/get';
import lodashCloneDeep from 'lodash/cloneDeep';
import {TYPE_ISSUES} from '../../constants/issue-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {getLinkedDocument, hasLinkedDocument} from '../../utils/issues-helpers';

export default class {
  constructor() {
    this.dispatch = null;
    this.defaultVisibility = true;
    this.creating = false;
    this.pushpinExtension = null;
    this.namespace = null;
    this.NEW_PUSHPIN_LABEL = i18n.t('newPushpinLabel');
    this.handlePushpinModified = this.handlePushpinModified.bind(this);
    this.handlePushpinCreated = this.handlePushpinCreated.bind(this);
    this.handleThumbnailCreated = this.handleThumbnailCreated.bind(this);
    this._createThumbnailHandler = this._createThumbnailHandler.bind(this);
    this.thumbnailCreateHandlers = {};
    this._handleExistingPushpinModified = this._handleExistingPushpinModified.bind(this);
    this._previousSelectedPushpinId = null;
    this.getExternalIds = this.getExternalIds.bind(this);
  }

  /**
   * Creates a unique handler instance for thumbnail creation event as this event can trigger
   * simultaneously for 2 (or more) different pushpins
   */
  _createThumbnailHandler(pushpinId) {
    this.thumbnailCreateHandlers[pushpinId] = thumbnailCreateEvent => {
      this.handleThumbnailCreated(thumbnailCreateEvent);
    };
    this.pushpinExtension.addEventListener(
      this.namespace.PUSH_PIN_CREATED_EVENT,
      this.thumbnailCreateHandlers[pushpinId]
    );
  }

  setPushpinExtension(pushpinExtension) {
    this.pushpinExtension = pushpinExtension;
    if (pushpinExtension) {
      this.namespace = AutodeskNamespace('Autodesk.BIM360.Extension.PushPin'); //TODO: ? eliminate reference to AutodeskNamespace global
    }
  }

  handlePushpinModified(event) {
    const {thumbnail, value} = event;

    // Mark pushpin as "new" so the pushpin middleware will know to clear it when the pushpin drop flow is ended
    value.set({isNew: true});
    // Make sure to extract `data` out of the event's value only after manipulating `isNew`
    let {data} = value;

    // If LMV version already supports it: Make sure that position and viewerStates are compatible between different viewing clients
    if (this.pushpinExtension && this.pushpinExtension.getLegacyPushPinData) {
      data = this.pushpinExtension.getLegacyPushPinData(value);
    }

    // We use cloneDeep here because LMV mutates nested objects within the event's data
    const pushpinAttributes = {...lodashCloneDeep(data)};
    this.dispatch(setPushpinAttributesAction(pushpinAttributes));

    // The viewer sends the thumbnail data as part of the pushpin modified event (e.g when a pushpin is
    // moved after being dropped, during creation)
    if (thumbnail) {
      this.dispatch(viewerPushpinThumbnailCreated({pushpinAttributes, thumbnail}));
    } else {
      // Bind event for handling the pushpin thumbnail data
      this._createThumbnailHandler(data.id);
    }
  }

  handlePushpinCreated(event) {
    const {
      value: {data}
    } = event;
    if (data.label === this.NEW_PUSHPIN_LABEL) {
      this.endCreateItem();
      this.setPushpinDraggable(data.id, true);
      this.handlePushpinModified(event);
    }
  }

  handleThumbnailCreated(event) {
    const pushpinAttributes = lodashGet(event, 'value.data');

    // Unbind event for handling the pushpin thumbnail data
    this.pushpinExtension.removeEventListener(
      this.namespace.PUSH_PIN_CREATED_EVENT,
      this.thumbnailCreateHandlers[pushpinAttributes.id]
    );
    delete this.thumbnailCreateHandlers[pushpinAttributes.id];

    this.dispatch(viewerPushpinThumbnailCreated({pushpinAttributes, thumbnail: event.thumbnail}));
  }

  setDispatch(dispatchFunction) {
    this.dispatch = dispatchFunction;
  }

  createPushpin(issue) {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension || !this.pushpinExtension.pushPinManager) {
      return;
    }

    if (hasLinkedDocument(issue)) {
      // When creating a pushpin, the current selection is automatically deselected by the viewer extension's internal logic.
      // We save the currently selected pushpin so we can select it after the creation of the new pushpin.
      const selectedItemId = lodashGet(this.pushpinExtension.pushPinManager.getSelectedItem(), 'data.id');

      const {
        details: {position, objectId, viewerState, externalId, attributesVersion}
      } = getLinkedDocument(issue);
      // Check if we are in 2D or 3D.
      // Note: Actually it would be cleaner to determine based on the issue instead of the current viewer state.
      //       But I didn't find anything in the issue to distinguish that. E.g., issue.pushPinAttributes.type turned out
      //       to be "TwoDVectorPushpin" for 3D issue, so it didn't help.
      const viewer = this.pushpinExtension.viewer;
      const model = viewer.model;
      const is3d = model && model.is3d();

      const pushPinData = {
        id: issue.id,
        label: issue[ISSUE_ATTRIBUTE_NAMES.ID],
        status: `${TYPE_ISSUES}-${issue.status}`,
        position: {x: position.x, y: position.y, z: position.z},
        type: TYPE_ISSUES,
        objectId,
        viewerState,
        externalId,
        attributesVersion
      };

      // For now, only use the new code path if LMV version supports it.
      if (is3d && this.pushpinExtension.loadItemFromLocal) {
        // For legacy reasons, issue positions are not directly stored in model-local coordinates. So we need some legacy conversion.
        this.pushpinExtension.legacyToLocalPushPinData(pushPinData);

        // Now, position and viewerState are in model-local coordinates.
        this.pushpinExtension.loadItemFromLocal(pushPinData);
      } else {
        // Old code path for 2D issues and older LMV versions
        this.pushpinExtension.loadItems([pushPinData]);
      }

      this.pushpinExtension.setVisibleById(issue.id, this.defaultVisibility);
      selectedItemId && this.pushpinExtension.selectOne(selectedItemId);
    }
  }

  setDefaultVisibility(visibility) {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension || !this.pushpinExtension.pushPinManager) {
      return;
    }

    this.defaultVisibility = visibility;
    const items = this.pushpinExtension.pushPinManager.items();
    items.forEach(item => {
      this.pushpinExtension.setVisibleById(item.data.id, this.defaultVisibility);
    });
  }

  updatePushpin(pushpin) {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension) {
      return;
    }

    const {id, label, status, position, objectId, viewerState, externalId, attributesVersion} = pushpin;
    const pushpinStatus = status.indexOf(TYPE_ISSUES) === -1 ? `${TYPE_ISSUES}-${status}` : status; // add type only if missing from status
    this.pushpinExtension.updateItemById(id, {
      id,
      label,
      status: pushpinStatus,
      position: {...position},
      type: TYPE_ISSUES,
      objectId,
      viewerState,
      externalId,
      attributesVersion
    });
    this.pushpinExtension.showAll();
  }

  _handleExistingPushpinModified({value, thumbnail}) {
    let {data} = value;

    // If LMV version already supports it: Make sure that position and viewerStates are compatible between different viewing clients
    if (this.pushpinExtension && this.pushpinExtension.getLegacyPushPinData) {
      data = this.pushpinExtension.getLegacyPushPinData(value);
    }
    this.dispatch(updateCurrentIssuePushpinAttributes(data));
    this.dispatch(viewerExistingIssuePushpinThumbnailCreated({pushpinAttributes: data, thumbnail}));
  }

  selectPushpin(pushpinId, canRelocatePushpin) {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension || !this.pushpinExtension.pushPinManager) {
      return;
    }

    if (this._previousSelectedPushpinId !== null && pushpinId !== this._previousSelectedPushpinId) {
      this.pushpinExtension.setDraggableById(this._previousSelectedPushpinId, false);
      this.pushpinExtension.removeEventListener(
        this.namespace.PUSH_PIN_MODIFY_EVENT,
        this._handleExistingPushpinModified
      );
    }
    this._previousSelectedPushpinId = pushpinId;

    const selectedItem = this.pushpinExtension.pushPinManager.getSelectedItem();
    const alreadySelected = selectedItem && selectedItem.data && selectedItem.data.id === pushpinId;

    // when all pushpins are rendered the viewer deselects all of them one by one.
    // we need to make sure that if we select a pushpin, we do that only after all pushpins are rendered
    // to avoid the pushpin deselection. we do that by calling setTimeout as a hack until the viewer team will
    // find a solution.
    setTimeout(() => {

      // Even if the pushPinManager existed in the outer selectPushpin() call, it might not exist anymore here.
      // It will be null if the current sheet was removed after calling setTimeout().
      // Note that this case actually happens on production in Design Collaboration (see https://jira.autodesk.com/browse/BIMCAS-18724)
      if (!this.pushpinExtension.pushPinManager) {
        return;
      }

      //  Check if listener is already added, otherwise add listener and make draggable
      if (
        !this.pushpinExtension.hasEventListener(
          this.namespace.PUSH_PIN_MODIFY_EVENT,
          this._handleExistingPushpinModified
        ) &&
        canRelocatePushpin
      ) {
        this.pushpinExtension.addEventListener(
          this.namespace.PUSH_PIN_MODIFY_EVENT,
          this._handleExistingPushpinModified
        );
        this.pushpinExtension.setDraggableById(pushpinId, true);
      }
      const pushpinAttributes = this.pushpinExtension.getItemById(pushpinId);
      this.dispatch(setPushpinAttributesAction(lodashCloneDeep(pushpinAttributes)));
      // In case its already selected from docs side
      if (alreadySelected) {
        return;
      }
      this.pushpinExtension.selectOne(pushpinId);
    }, 0);
  }

  setPushpinDraggable(pushpinId, draggable) {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension) {
      return;
    }

    this.setDefaultVisibility(!draggable);
    // if it's draggable, it should be visible:
    this.pushpinExtension.setVisibleById(pushpinId, draggable || this.defaultVisibility);
    this.pushpinExtension.setDraggableById(pushpinId, draggable);
    if (draggable) {
      this.pushpinExtension.addEventListener(this.namespace.PUSH_PIN_MODIFY_EVENT, this.handlePushpinModified);
    } else {
      this.pushpinExtension.removeEventListener(this.namespace.PUSH_PIN_MODIFY_EVENT, this.handlePushpinModified);
    }
  }

  deselectPushpins() {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension) {
      return;
    }

    if (this._previousSelectedPushpinId !== null) {
      this.pushpinExtension.setDraggableById(this._previousSelectedPushpinId, false);
      this._previousSelectedPushpinId = null;
      this.pushpinExtension.removeEventListener(
        this.namespace.PUSH_PIN_MODIFY_EVENT,
        this._handleExistingPushpinModified
      );
    }

    this.pushpinExtension.selectNone();
    this.dispatch(setPushpinAttributesAction(null));
  }

  clearSinglePushpin(attributes) {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension) {
      return;
    }

    this.pushpinExtension.removeItemById(attributes.id);
  }

  startDropPushpin(issueStatus) {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension) {
      return;
    }

    this.setDefaultVisibility(false);

    const pushpinStatus = `${TYPE_ISSUES}-${issueStatus}`;
    // Why does the LMV startCreateItem method need issue-type encoded in status:pushpinStatus?
    // (startCreateItem already receives type:issueType as its own parameter)
    this.pushpinExtension.startCreateItem({
      label: this.NEW_PUSHPIN_LABEL,
      status: pushpinStatus,
      type: TYPE_ISSUES
    });
    this.creating = true;
    // Bind event for handling pushpin drop
    this.pushpinExtension.addEventListener(this.namespace.PUSH_PIN_CREATED_EVENT, this.handlePushpinCreated);
  }

  endCreateItem() {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension) {
      return;
    }

    if (this.creating) {
      // LMV .endCreateItem() sets visibility of all pushpins to true:
      this.pushpinExtension.endCreateItem();
      this.creating = false;
    }
  }

  endDropPushpin() {
    // Don't do anything if we don't have a pushpin extension
    if (!this.pushpinExtension) {
      return;
    }

    this.setDefaultVisibility(true);
    this.pushpinExtension.removeEventListener(this.namespace.PUSH_PIN_CREATED_EVENT, this.handlePushpinCreated);
    this.pushpinExtension.removeEventListener(this.namespace.PUSH_PIN_MODIFY_EVENT, this.handlePushpinModified);
    this.dispatch(setPushpinAttributesAction(null));
    this.endCreateItem();
  }

  getModelUnit() {
    return this.pushpinExtension?.viewer?.model?.getUnitString();
  }

  getModelType() {
    return this.pushpinExtension?.viewer?.model?.getData()?.loadOptions?.fileExt;
  }

  getExternalIds(objectIds) {
    return this.pushpinExtension?.viewer?.model?.getBulkProperties2(objectIds, {needsExternalId: true}, res => res);
  }

}
